import type { RagaRecord } from '@swarasense/shared';

/**
 * Resolves a `?raga=` deep-link query-param value (content SEO pages, Task 5)
 * to a known raga record. Pure and framework-free — `app.ts`'s init reads the
 * live query string and the loaded seed bundle's record list and calls this;
 * everything else (URL parsing, `store.set`) stays in `app.ts` so this stays
 * cheap to unit-test in isolation, the same split `ragam-search.ts` uses.
 *
 * Matching: exact `name` first, then `aliases`, both case-insensitive and
 * trimmed (a raga page's own CTA — see `raga-page.ts`'s `ctaHref` —
 * `encodeURIComponent`s the raga's exact `name`, so the exact-match branch is
 * the common case; alias matching is a courtesy for a hand-typed/aliased URL).
 * `null`/empty/unknown all resolve to `null` — the caller's job is to leave
 * the current selection unchanged in that case, not to throw or guess.
 */
export function resolveRagaParam(
  param: string | null,
  records: readonly RagaRecord[],
): RagaRecord | null {
  if (param === null) return null;
  const q = param.trim().toLowerCase();
  if (q === '') return null;

  for (const record of records) {
    if (record.name.toLowerCase() === q) return record;
  }
  for (const record of records) {
    if (record.aliases.some((alias) => alias.toLowerCase() === q)) return record;
  }
  return null;
}
